import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { format, parseISO, isValid } from 'date-fns';
import { LuCircleCheck, LuClock, LuFileText, LuMessageSquare, LuPlus, LuSend, LuX, LuInbox, LuHash, LuCalendar } from 'react-icons/lu';

import api, { protocolosAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ErroLoadData from '../components/ErroLoadData';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: calc(100vh - 200px);

  @media (max-width: 768px) {
    padding: 16px 12px 24px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 28px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 18px;
  color: ${props => props.theme.colors.grayMedium};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 18px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Card = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 12px;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 800;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: -0.2px;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${props => props.theme.colors.grayMedium};
  font-weight: 500;

  svg {
    color: ${props => props.theme.colors.secondary};
    opacity: 0.8;
  }

  span {
    color: ${props => props.theme.colors.grayDark};
    font-weight: 700;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.$bg};
  color: ${props => props.$color};
  border: 1px solid ${props => props.$borderColor};
  white-space: nowrap;
`;

const ObservacaoContainer = styled.div`
  margin-top: 12px;
`;

const ObservacaoLabel = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: ${props => props.theme.colors.grayMedium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ObservacaoBox = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  color: #334155;
  font-size: 13px;
  line-height: 1.6;
  border-left: 3px solid #e2e8f0;
  white-space: pre-wrap;
  font-family: inherit;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
`;

const ModalCard = styled(motion.div)`
  width: min(620px, 100%);
  max-height: calc(100vh - 40px);
  overflow: auto;
  background: ${props => props.theme.colors.white};
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  h2 {
    margin: 0;
    color: ${props => props.theme.colors.primary};
    font-size: 22px;
    font-weight: 700;
  }

  p {
    margin: 6px 0 0;
    color: ${props => props.theme.colors.grayMedium};
    font-size: 14px;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: ${props => props.theme.colors.grayLight};
  color: ${props => props.theme.colors.grayDark};
  border-radius: 9999px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGrid = styled.form`
  display: grid;
  gap: 16px;
`;

const Field = styled.label`
  display: grid;
  gap: 8px;
  color: ${props => props.theme.colors.primary};
  font-size: 14px;
  font-weight: 700;

  select,
  textarea {
    width: 100%;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 10px;
    padding: 12px;
    color: ${props => props.theme.colors.grayDark};
    font: inherit;
    resize: vertical;
    background: ${props => props.theme.colors.white};
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.secondary};
      box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
    }
  }

  textarea {
    min-height: 110px;
  }
`;

const CharHint = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => (props.$ok ? props.theme.colors.success : props.theme.colors.grayMedium)};
`;

const ConfirmMessage = styled.p`
  margin: 0 0 20px;
  color: ${props => props.theme.colors.grayDark};
  font-size: 16px;
  line-height: 1.6;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 14px 24px;
  background: ${props => props.theme.colors.gradientPrimary};
  color: ${props => props.theme.colors.white};
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 15px -3px rgba(30, 64, 175, 0.25);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 15px 20px -3px rgba(30, 64, 175, 0.35);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
  }
`;

const AddProtocolButton = styled.button`
  background: ${props => props.theme.colors.gradientPrimary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  transition: all 0.3s ease;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 20px auto 0;
  box-shadow: 0 10px 15px -3px rgba(30, 64, 175, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 20px -3px rgba(30, 64, 175, 0.35);
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 360px;
  }
`;

const Timeline = styled.div`
  display: grid;
  gap: 16px;
  margin-bottom: 24px;
`;

const TimelineItem = styled.div`
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 14px;
`;

const TimelineIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${props => props.$done ? '#dcfce7' : '#eff6ff'};
  color: ${props => props.$done ? '#166534' : '#1d4ed8'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => props.$done ? '#bbf7d0' : '#dbeafe'};
`;

const TimelineContent = styled.div`
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  padding: 12px 14px 8px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  min-height: 52px;

  strong {
    display: block;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 2px;
    line-height: 1.4;
    font-size: 14px;
  }

  p {
    margin: 0;
    color: ${props => props.theme.colors.grayDark};
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
  }
`;

const TimelineBubbleBody = styled.div`
  flex: 1;
`;

const TimelineDateTime = styled.time`
  display: block;
  align-self: flex-end;
  margin-top: 6px;
  color: ${props => props.theme.colors.grayMedium};
  font-size: 11px;
  font-weight: 500;
  text-align: right;
  line-height: 1.2;
  white-space: nowrap;
`;

const TimelineSubtitle = styled.span`
  display: block;
  margin: -2px 0 6px;
  color: ${props => props.theme.colors.grayMedium};
  font-size: 13px;
  font-weight: 500;
`;

const InlineEmpty = styled.div`
  padding: 20px;
  border-radius: 12px;
  background: #f1f5f9;
  color: #475569;
  text-align: center;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 32px;
  text-align: center;
  max-width: 550px;
  margin: 40px auto;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);

  svg {
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 24px;
    opacity: 0.2;
  }

  h3 {
    color: ${props => props.theme.colors.primary};
    font-size: 22px;
    font-weight: 800;
    margin: 0 0 12px;
  }

  p {
    color: ${props => props.theme.colors.grayMedium};
    font-size: 16px;
    line-height: 1.6;
    margin: 0;
    max-width: 360px;
  }
`;

function getStatusConfig(theme, status) {
  const s = (status || '').toUpperCase();

  if (s.includes('ABERTO') || s.startsWith('1.')) {
    return {
      bg: '#fff7ed',
      color: '#c2410c',
      borderColor: '#ffedd5',
      icon: <LuClock size={14} />,
      label: 'ABERTO',
      cardColor: theme.colors.secondary
    };
  }

  if (s.includes('ATENDIMENTO') || s.startsWith('2.')) {
    return {
      bg: '#eff6ff',
      color: '#1d4ed8',
      borderColor: '#dbeafe',
      icon: <LuFileText size={14} />,
      label: 'EM ANDAMENTO',
      cardColor: '#3b82f6'
    };
  }

  if (s.includes('CONCLU') || s.startsWith('3.')) {
    return {
      bg: '#f0fdf4',
      color: '#15803d',
      borderColor: '#dcfce7',
      icon: <LuCircleCheck size={14} />,
      label: 'CONCLUÍDO',
      cardColor: '#22c55e'
    };
  }

  return {
    bg: '#f8fafc',
    color: '#475569',
    borderColor: '#e2e8f0',
    icon: <LuFileText size={14} />,
    label: status || 'OUTRO',
    cardColor: '#cbd5e1'
  };
}

const StatusBadge = ({ status }) => {
  const theme = useTheme();
  const config = getStatusConfig(theme, status);
  
  return (
    <Badge $bg={config.bg} $color={config.color} $borderColor={config.borderColor}>
      {config.icon}
      {config.label}
    </Badge>
  );
};

function formatProtocolDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const parsed = parseISO(dateStr);
    if (!isValid(parsed)) return '-';
    return format(parsed, 'dd/MM/yyyy');
  } catch {
    return '-';
  }
}

function formatProtocolDateTime(dateStr) {
  if (!dateStr) return null;
  try {
    const parsed = parseISO(dateStr);
    if (!isValid(parsed)) return null;
    return format(parsed, "dd/MM/yyyy 'às' HH:mm");
  } catch {
    return null;
  }
}

function formatTrackingDateTime(andamento) {
  // HORA_ACOMPANHAMENTO vem como ISO completo: "2026-05-27T15:56:55.850Z"
  const dateTimeCandidates = [
    andamento?.HORA_ACOMPANHAMENTO,
    andamento?.DATA_HORA_ACOMPANHAMENTO,
    andamento?.hora,
    andamento?.dataHora,
  ];

  for (const value of dateTimeCandidates) {
    if (!value) continue;
    const formatted = formatProtocolDateTime(value);
    if (formatted) return formatted;
  }

  const data = andamento?.DATA_ACOMPANHAMENTO || andamento?.data;
  if (data) {
    const dataFormatada = formatProtocolDate(String(data).split('T')[0]);
    return dataFormatada !== '-' ? dataFormatada : null;
  }

  return null;
}

function getTrackingDateTimeAttr(andamento) {
  return andamento?.HORA_ACOMPANHAMENTO
    || andamento?.DATA_HORA_ACOMPANHAMENTO
    || andamento?.DATA_ACOMPANHAMENTO
    || undefined;
}

const motivoOptions = [
  { value: '2_VIA_BOLETO', label: '2ª via de boleto' },
  { value: 'ALTERACAO_CADASTRAL', label: 'Alteração cadastral' },
  { value: 'ALTERACAO_CONTRATO', label: 'Alteração de contrato' },
  { value: 'DEPENDENTES', label: 'Inclusão ou remoção de dependentes' },
  { value: 'COBERTURA_PLANO', label: 'Dúvidas sobre cobertura do plano' },
  { value: 'PAGAMENTOS', label: 'Pagamentos e cobranças' },
  { value: 'CANCELAMENTO', label: 'Cancelamento' },
  { value: 'OUTROS', label: 'Outros assuntos' },
];

const MENSAGEM_CANCELAMENTO = 'Recebemos sua solicitação de cancelamento. Um atendente entrará em contato através dos meios de contato cadastrados.';
const MIN_DESCRICAO_CHARS = 10;

function isClientTracking(andamento) {
  const usuario = String(andamento?.USUARIO_ACOMPANHAMENTO || andamento?.usuario || '').trim().toUpperCase();
  return usuario === 'APLICATIVO CLIENTE';
}

function getTrackingContent(andamento) {
  if (isClientTracking(andamento)) {
    return {
      title: 'Cliente',
      subtitle: null,
      description: andamento?.DESCRICAO_ACOMPANHAMENTO?.trim() || null,
    };
  }

  const usuario = String(andamento?.USUARIO_ACOMPANHAMENTO || andamento?.usuario || '').trim();
  const descricaoWeb = andamento?.DESCRICAO_WEB_ACOMPANHAMENTO?.trim();
  const subtitle = usuario || null;

  if (descricaoWeb) {
    return {
      title: 'Santa Casa',
      subtitle,
      description: descricaoWeb,
    };
  }

  return {
    title: 'Santa Casa',
    subtitle,
    description: 'Sua solicitação está sendo trabalhada.',
  };
}

function isProtocolOpenFromStatus(status) {
  const s = (status || '').toUpperCase();
  // cobre casos comuns: "3. CONCLUÍDO", "CONCLUIDO", "FECHADO", etc.
  if (s.includes('CONCLU') || s.includes('FECHAD') || s.includes('ENCERRAD') || s.includes('FINALIZ')) return false;
  return true;
}

const Solicitacoes = () => {
  const theme = useTheme();
  const { user, selectedContrato } = useAuth();
  const queryClient = useQueryClient();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedProtocolo, setSelectedProtocolo] = useState(null);
  const [andamentosFetchNonce, setAndamentosFetchNonce] = useState(0);
  const [motivo, setMotivo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [novoAndamento, setNovoAndamento] = useState('');
  const [cancelamentoConfirmOpen, setCancelamentoConfirmOpen] = useState(false);

  const { data, isLoading, error } = useQuery(
    ['dashboard', user?.documento, selectedContrato?.numero],
    async () => {
      const response = await api.get('/cliente/dashboard');
      return response.data.data;
    },
    {
      enabled: !!user?.documento,
      staleTime: 2 * 60 * 1000,
      onError: () => toast.error('Erro ao carregar solicitações. Verifique o contrato selecionado.'),
    }
  );

  const selectedProtocolCode = selectedProtocolo?.codigo || null;
  const selectedProtocolId = selectedProtocolo?.id ?? null;
  const protocoloAberto = isProtocolOpenFromStatus(selectedProtocolo?.status);

  const handleSelectProtocolo = (protocolo) => {
    setSelectedProtocolo(protocolo);
    setAndamentosFetchNonce((current) => current + 1);
  };

  const {
    data: andamentos = [],
    isLoading: loadingAndamentos,
    refetch: refetchAndamentos,
  } = useQuery(
    ['protocolos-andamentos', selectedProtocolCode, selectedProtocolId, selectedContrato?.numero, andamentosFetchNonce],
    async () => {
      const response = await protocolosAPI.getAndamentos(selectedProtocolCode, selectedProtocolId);
      return response.data.data || [];
    },
    {
      enabled: !!selectedProtocolCode && selectedProtocolId != null,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      onError: (err) => {
        const message = err.response?.data?.message || 'Erro ao carregar andamentos do protocolo.';
        toast.error(message);
      },
    }
  );

  const criarProtocoloMutation = useMutation(
    ({ motivo: motivoSelecionado, descricao: descricaoInformada }) => protocolosAPI.criar({
      motivo: motivoSelecionado,
      descricao: descricaoInformada,
    }),
    {
      onSuccess: (response, variables) => {
        const isCancelamento = variables?.motivo === 'CANCELAMENTO';
        setCreateModalOpen(false);
        setMotivo('');
        setDescricao('');
        queryClient.invalidateQueries(['dashboard']);

        if (isCancelamento) {
          setCancelamentoConfirmOpen(true);
          return;
        }

        toast.success(response.data?.message || 'Protocolo criado com sucesso.');
      },
      onError: (err) => {
        const message = err.response?.data?.message || 'Não foi possível criar o protocolo.';
        toast.error(message);
      },
    }
  );

  const adicionarAndamentoMutation = useMutation(
    () => protocolosAPI.adicionarAndamento(selectedProtocolCode, {
      id: selectedProtocolId,
      descricao: novoAndamento,
    }),
    {
      onSuccess: (response) => {
        const message = response.data?.message || 'Acompanhamento enviado com sucesso.';
        toast.success(message);
        setNovoAndamento('');

        const andamentosAtualizados = response.data?.data;
        if (Array.isArray(andamentosAtualizados)) {
          queryClient.setQueryData(
            ['protocolos-andamentos', selectedProtocolCode, selectedProtocolId, selectedContrato?.numero, andamentosFetchNonce],
            andamentosAtualizados
          );
        } else {
          refetchAndamentos();
        }
      },
      onError: (err) => {
        const message = err.response?.data?.message || 'Não foi possível enviar o acompanhamento.';
        toast.error(message);
      },
    }
  );

  const handleCriarProtocolo = (event) => {
    event.preventDefault();

    if (!motivo) {
      toast.warn('Selecione o motivo do protocolo.');
      return;
    }

    if (descricao.trim().length < MIN_DESCRICAO_CHARS) {
      toast.warn(`Informe uma descrição com pelo menos ${MIN_DESCRICAO_CHARS} caracteres.`);
      return;
    }

    criarProtocoloMutation.mutate({ motivo, descricao });
  };

  const handleAdicionarAndamento = (event) => {
    event.preventDefault();

    if (!protocoloAberto) {
      toast.warn('Este protocolo já foi concluído e não permite novos acompanhamentos.');
      return;
    }

    if (novoAndamento.trim().length < 3) {
      toast.warn('Informe o texto do acompanhamento.');
      return;
    }

    adicionarAndamentoMutation.mutate();
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <Container>
        <ErroLoadData />
      </Container>
    );
  }

  const protocolos = (data?.protocolos || [])
    .slice()
    .sort((a, b) => {
      const ta = a?.data ? Date.parse(a.data) : NaN;
      const tb = b?.data ? Date.parse(b.data) : NaN;

      const aValid = Number.isFinite(ta);
      const bValid = Number.isFinite(tb);

      if (!aValid && !bValid) return 0;
      if (!aValid) return 1; // inválidos por último
      if (!bValid) return -1;

      return tb - ta; // mais recente primeiro
    });

  const containerVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, staggerChildren: 0.06 } },
  };

  const itemVariants = { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Container>
        <PageHeader>
          <PageTitle>Solicitações</PageTitle>
          <PageSubtitle>Acompanhe seus protocolos e o andamento das solicitações</PageSubtitle>
          <AddProtocolButton type="button" onClick={() => setCreateModalOpen(true)}>
            <LuPlus size={20} />
            Nova solicitação
          </AddProtocolButton>
        </PageHeader>

        {!protocolos.length ? (
          <EmptyState>
            <LuInbox size={80} strokeWidth={1.5} />
            <h3>Nenhuma solicitação encontrada</h3>
            <p>Você ainda não possui protocolos abertos para este contrato. Suas solicitações aparecerão aqui.</p>
          </EmptyState>
        ) : (
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            <CardsGrid>
              {protocolos.map((p, idx) => (
                    <Card
                      key={`${p.codigo || 'protocolo'}-${idx}`}
                      variants={itemVariants}
                      role="button"
                      tabIndex={0}
                      $statusColor={getStatusConfig(theme, p.status).cardColor}
                      onClick={() => handleSelectProtocolo(p)}
                    >
                      <CardHeader>
                        <CardTitle>{p.solicitacao || 'Solicitação'}</CardTitle>
                        <StatusBadge status={p.status} />
                      </CardHeader>

                      <MetaInfo>
                        <MetaItem>
                          <LuHash size={16} />
                          Protocolo: <span>{p.codigo || '-'}</span>
                        </MetaItem>
                        <MetaItem>
                          <LuCalendar size={16} />
                          Data: <span>{formatProtocolDate(p.data)}</span>
                        </MetaItem>
                      </MetaInfo>

                      {p.observacao && (
                        <ObservacaoContainer>
                          <ObservacaoLabel>
                            <LuMessageSquare size={14} /> Observação
                          </ObservacaoLabel>
                          <ObservacaoBox>
                            {p.observacao}
                          </ObservacaoBox>
                        </ObservacaoContainer>
                      )}
                    </Card>
              ))}
            </CardsGrid>
          </motion.div>
        )}

        {createModalOpen && (
          <ModalOverlay>
            <ModalCard
              initial={{ opacity: 0, y: -20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              onClick={(event) => event.stopPropagation()}
            >
              <ModalHeader>
                <div>
                  <h2>Novo protocolo</h2>
                  <p>Ao enviar, um protocolo será gerado para que a equipe da Santa Casa analise e responda ao seu pedido.</p>
                </div>
                <CloseButton type="button" aria-label="Fechar" onClick={() => setCreateModalOpen(false)}>
                  <LuX size={20} />
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <FormGrid onSubmit={handleCriarProtocolo}>
                  <Field>
                    Motivo
                    <select value={motivo} onChange={(event) => setMotivo(event.target.value)}>
                      <option value="">Selecione uma opção</option>
                      {motivoOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field>
                    Descrição
                    <textarea
                      value={descricao}
                      onChange={(event) => setDescricao(event.target.value)}
                      placeholder="Informe os detalhes da sua solicitação"
                    />
                    <CharHint $ok={descricao.trim().length >= MIN_DESCRICAO_CHARS}>
                      {descricao.trim().length >= MIN_DESCRICAO_CHARS
                        ? `${descricao.trim().length} caracteres`
                        : `Mínimo de ${MIN_DESCRICAO_CHARS} caracteres (${descricao.trim().length}/${MIN_DESCRICAO_CHARS})`}
                    </CharHint>
                  </Field>

                  <PrimaryButton
                    type="submit"
                    disabled={
                      criarProtocoloMutation.isLoading
                      || !motivo
                      || descricao.trim().length < MIN_DESCRICAO_CHARS
                    }
                  >
                    <LuFileText size={17} />
                    {criarProtocoloMutation.isLoading ? 'Criando...' : 'Criar protocolo'}
                  </PrimaryButton>
                </FormGrid>
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}

        {cancelamentoConfirmOpen && (
          <ModalOverlay>
            <ModalCard
              initial={{ opacity: 0, y: -20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancelamento-confirm-title"
            >
              <ModalHeader>
                <div>
                  <h2 id="cancelamento-confirm-title">Solicitação de cancelamento</h2>
                </div>
                <CloseButton type="button" aria-label="Fechar" onClick={() => setCancelamentoConfirmOpen(false)}>
                  <LuX size={20} />
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <ConfirmMessage>{MENSAGEM_CANCELAMENTO}</ConfirmMessage>
                <PrimaryButton type="button" onClick={() => setCancelamentoConfirmOpen(false)}>
                  Entendi
                </PrimaryButton>
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}

        {selectedProtocolo && (
          <ModalOverlay>
            <ModalCard
              initial={{ opacity: 0, y: -20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              onClick={(event) => event.stopPropagation()}
            >
              <ModalHeader>
                <div>
                  <h2>Andamentos do protocolo</h2>
                  <p>Protocolo {selectedProtocolCode || '-'} - {selectedProtocolo.solicitacao || 'Solicitação'}</p>
                </div>
                <CloseButton type="button" aria-label="Fechar" onClick={() => setSelectedProtocolo(null)}>
                  <LuX size={20} />
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                {loadingAndamentos ? (
                  <InlineEmpty>Carregando andamentos...</InlineEmpty>
                ) : andamentos.length ? (
                  <Timeline>
                    {andamentos.map((andamento, index) => {
                      const done = String(andamento?.TAREFA_CONCLUIDA_ACOMPANHAMENTO || andamento?.concluido || '') === '1';
                      const { title, subtitle, description } = getTrackingContent(andamento);
                      const dataHoraFormatada = formatTrackingDateTime(andamento);
                      const dateTimeAttr = getTrackingDateTimeAttr(andamento);
                      return (
                        <TimelineItem key={`${andamento?.ID_ACOMPANHAMENTO || 'andamento'}-${index}`}>
                          <TimelineIcon $done={done}>
                            {done ? <LuCircleCheck size={17} /> : <LuMessageSquare size={17} />}
                          </TimelineIcon>
                          <TimelineContent>
                            <TimelineBubbleBody>
                              {title && <strong>{title}</strong>}
                              {subtitle && <TimelineSubtitle>{subtitle}</TimelineSubtitle>}
                              {description && <p>{description}</p>}
                            </TimelineBubbleBody>
                            {dataHoraFormatada && (
                              <TimelineDateTime dateTime={dateTimeAttr}>{dataHoraFormatada}</TimelineDateTime>
                            )}
                          </TimelineContent>
                        </TimelineItem>
                      );
                    })}
                  </Timeline>
                ) : (
                  <InlineEmpty>Nenhum andamento encontrado para este protocolo.</InlineEmpty>
                )}

                <FormGrid onSubmit={handleAdicionarAndamento}>
                  <Field>
                    Adicionar acompanhamento
                    <textarea
                      value={novoAndamento}
                      onChange={(event) => setNovoAndamento(event.target.value)}
                      disabled={!protocoloAberto}
                      placeholder="Envie uma nova informação ou dúvida sobre este protocolo"
                    />
                  </Field>
                  <PrimaryButton
                    type="submit"
                    disabled={adicionarAndamentoMutation.isLoading || !selectedProtocolCode || selectedProtocolId == null || !protocoloAberto}
                  >
                    <LuSend size={17} />
                    {adicionarAndamentoMutation.isLoading ? 'Enviando...' : 'Enviar acompanhamento'}
                  </PrimaryButton>
                </FormGrid>
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}
      </Container>
    </motion.div>
  );
};


export default Solicitacoes;
